import { useEffect } from 'react';

const SITE_URL = 'https://nexusrmm.ai';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

// First path segment -> section label, for the SECOND breadcrumb level — only sections that
// have a real index page at /<segment> belong here (buildBreadcrumbJsonLd links to that page).
// /solutions/* has no /solutions index page, so it's deliberately absent — those routes fall
// back to a flat Home > <title> breadcrumb rather than link a crumb to a 404.
const SECTION_INDEX_PAGES: Record<string, string> = {
  platform: 'Platform',
  compliance: 'Compliance',
  compare: 'Compare',
  integrations: 'Integrations',
  glossary: 'Glossary',
  blog: 'Blog',
};

type JsonLdNode = Record<string, unknown>;

/**
 * Auto-derives a BreadcrumbList from the route path, so every interior page gets correct
 * breadcrumb structured data without each page having to hand-author one. Home page (path "/")
 * gets none — it's the root, and the base Organization/WebSite graph in index.html covers it.
 */
function buildBreadcrumbJsonLd(path: string, title: string): JsonLdNode | null {
  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  const items: JsonLdNode[] = [{ '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` }];
  const sectionLabel = SECTION_INDEX_PAGES[segments[0]];
  if (segments.length > 1 && sectionLabel) {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: sectionLabel,
      item: `${SITE_URL}/${segments[0]}`,
    });
  }
  // Last crumb is the current page — no "item" URL, per Google's breadcrumb guidance.
  items.push({ '@type': 'ListItem', position: items.length + 1, name: title });

  return { '@type': 'BreadcrumbList', '@id': `${SITE_URL}${path}#breadcrumb`, itemListElement: items };
}

export type SeoInput = {
  title: string;
  description: string;
  /** Path only, e.g. "/platform/endpoint-management-rmm" — must match scripts/prerender.mjs' ROUTES manifest. */
  path: string;
  /**
   * Extra schema.org node(s) specific to this page (e.g. BlogPosting, DefinedTerm) — combined
   * with the auto-generated BreadcrumbList into one @graph. Omit for pages that need only the
   * breadcrumb (most of the site).
   */
  schema?: JsonLdNode | JsonLdNode[];
  /**
   * Set true to mark this page non-indexable (currently just NotFound). Cloudflare's SPA asset
   * fallback (wrangler.jsonc's not_found_handling) serves index.html with a 200 for ANY
   * unmatched path so client-side routing can render a real 404 — the HTTP status can't be
   * fixed without breaking deep-linking, so this is the mitigation available at the app layer:
   * flips the robots meta so a JS-executing crawler doesn't index the 200-status 404 content.
   * Always reset on every render (not just when true) since this is an SPA — a visitor can
   * navigate from the 404 straight to a real page, and the meta tag must flip back.
   */
  noindex?: boolean;
  /**
   * Present only for article-type pages (blog posts) — flips og:type to "article" and adds the
   * Open Graph article namespace tags (published/author). authorUrl, not a plain name, per the
   * article:author spec (expects a profile URL) — reuses the same URL as the page's BlogPosting
   * JSON-LD author. Omit for every other page (stays og:type=website, the index.html default).
   */
  article?: { publishedTime: string; authorUrl: string };
};

function setMeta(selector: string, attr: 'content' | 'href', value: string): void {
  const el = document.head.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

// Google typically renders ~155-160 characters of a meta description before truncating with an
// ellipsis, often mid-word. Content fields (blog excerpts, glossary/integration/compliance
// summaries) are written for on-page display first, where a longer, complete sentence reads
// better — so rather than shortening those fields (and degrading the page copy), the meta/OG/
// Twitter description is independently clamped here, at a word boundary, to what search engines
// and social unfurlers will actually show without cutting a word in half.
const META_DESCRIPTION_MAX = 157;

function clampDescription(text: string): string {
  if (text.length <= META_DESCRIPTION_MAX) return text;
  const truncated = text.slice(0, META_DESCRIPTION_MAX);
  const lastSpace = truncated.lastIndexOf(' ');
  return `${truncated.slice(0, lastSpace > 0 ? lastSpace : META_DESCRIPTION_MAX)}…`;
}

// For OG properties that aren't always present (article:published_time/author only apply to
// blog posts) — unlike setMeta above, which updates an element already declared in index.html,
// this creates the element on demand and removes it when the value goes away (e.g. navigating
// from a blog post to a non-article page), same lifecycle as the #page-jsonld script tag below.
function setOrRemoveMeta(id: string, property: string, value: string | undefined): void {
  let el = document.getElementById(id) as HTMLMetaElement | null;
  if (value) {
    if (!el) {
      el = document.createElement('meta');
      el.id = id;
      el.setAttribute('property', property);
      document.head.appendChild(el);
    }
    el.setAttribute('content', value);
  } else if (el) {
    el.remove();
  }
}

/**
 * Client-side per-page <title>/meta/JSON-LD sync for the SPA. The actual crawler-visible values
 * are baked at build time by scripts/prerender.mjs (which reads them back out of the live DOM
 * after this hook has run, including the #page-jsonld script below — see that file); this hook
 * exists so a real visitor's tab title/meta stay correct across client-side navigations, and so
 * JS-executing scrapers (Googlebot's second pass, share-link unfurlers that render) see the
 * right values too.
 */
export function useSeo({ title, description, path, schema, noindex, article }: SeoInput): void {
  const schemaKey = schema ? JSON.stringify(schema) : '';

  useEffect(() => {
    const fullTitle = `${title} | Nexus`;
    const url = `${SITE_URL}${path}`;
    const clampedDescription = clampDescription(description);
    document.title = fullTitle;
    setMeta('meta[name="description"]', 'content', clampedDescription);
    setMeta('meta[name="robots"]', 'content', noindex ? 'noindex, follow' : 'index, follow');
    setMeta('link[rel="canonical"]', 'href', url);
    setMeta('meta[property="og:type"]', 'content', article ? 'article' : 'website');
    setMeta('meta[property="og:title"]', 'content', fullTitle);
    setMeta('meta[property="og:description"]', 'content', clampedDescription);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[property="og:image"]', 'content', DEFAULT_OG_IMAGE);
    setMeta('meta[name="twitter:title"]', 'content', fullTitle);
    setMeta('meta[name="twitter:description"]', 'content', clampedDescription);
    setMeta('meta[name="twitter:image"]', 'content', DEFAULT_OG_IMAGE);
    setOrRemoveMeta('meta-article-published-time', 'article:published_time', article?.publishedTime);
    setOrRemoveMeta('meta-article-author', 'article:author', article?.authorUrl);

    const breadcrumb = buildBreadcrumbJsonLd(path, title);
    const extra = schema ? (Array.isArray(schema) ? schema : [schema]) : [];
    const graph = [...(breadcrumb ? [breadcrumb] : []), ...extra];

    let script = document.getElementById('page-jsonld') as HTMLScriptElement | null;
    if (graph.length > 0) {
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = 'page-jsonld';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
    } else if (script) {
      script.remove();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, schemaKey, noindex, article?.publishedTime, article?.authorUrl]);
}
